import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMyProfile, useUpdateMyProfile, type MyProfile, type ProfileUpdate } from './queries';

/** The five screens of the guided setup (PRF-08). How many there are is this app's business. */
export const TOTAL_STEPS = 5;

/**
 * How long she has to stop typing before her answer is sent. Short enough that walking away from a
 * half-typed sentence keeps it, long enough that a sentence is not thirty requests.
 */
const SAVE_AFTER_MS = 700;

export interface ProfileEditor {
  /** Her profile with anything not yet acknowledged by the server already applied. */
  profile: MyProfile | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  patch: (changes: ProfileUpdate) => void;
  /** True while something is queued or in flight, which the questionnaire says out loud. */
  isSaving: boolean;
  /** The last save did not land. Her answers are still on screen and will be retried. */
  saveFailed: boolean;
}

/**
 * The questionnaire's connection to `/api/profile`. PRF-08 promises a setup that can be abandoned
 * halfway and picked up later — on another device, which is the part a browser cannot keep — so
 * every answer is sent as it is given rather than gathered into one submit at the end.
 *
 * Two things it has to get right. Answers she has typed but that have not been acknowledged yet stay
 * on screen, so the form never flickers back to an older value between keystroke and response. And
 * saves are one at a time: two overlapping requests can land in either order, and the loser would
 * overwrite what she typed last.
 */
export function useProfileEditor(): ProfileEditor {
  const query = useMyProfile();
  const save = useUpdateMyProfile();

  const [pending, setPending] = useState<ProfileUpdate>({});

  // Read during cleanup and from event handlers that outlive the render they were made in.
  const mutate = useRef(save.mutate);
  mutate.current = save.mutate;
  const latest = useRef(pending);
  latest.current = pending;

  const patch = useCallback((changes: ProfileUpdate) => {
    setPending((current) => ({ ...current, ...changes }));
  }, []);

  useEffect(() => {
    if (Object.keys(pending).length === 0 || save.isPending) {
      return;
    }

    // Rescheduled on every keystroke, which is the whole debounce: the timer only elapses once she
    // has paused. When a save is in flight this effect runs again as it settles, and sends the rest.
    const timer = window.setTimeout(() => {
      mutate.current(pending, {
        // Only the keys that were sent, and only if they have not changed since — a letter typed
        // while the request was in the air must not be dropped along with the answer to it.
        onSuccess: () => setPending((current) => withoutSettled(current, pending)),
      });
    }, SAVE_AFTER_MS);

    return () => window.clearTimeout(timer);
  }, [pending, save.isPending]);

  useEffect(() => {
    // Leaving the questionnaire is the common way to end a session with it, and a phone in a
    // conference hall loses its tab without warning. Both skip the debounce.
    const flush = () => {
      if (Object.keys(latest.current).length > 0) {
        mutate.current(latest.current);
      }
    };

    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);

  const profile = useMemo(() => merge(query.data, pending), [query.data, pending]);

  const refetch = useCallback(() => void query.refetch(), [query]);

  return {
    profile,
    isPending: query.isPending,
    isError: query.isError,
    refetch,
    patch,
    isSaving: save.isPending || Object.keys(pending).length > 0,
    saveFailed: save.isError,
  };
}

function merge(profile: MyProfile | undefined, pending: ProfileUpdate): MyProfile | undefined {
  if (!profile) {
    return undefined;
  }

  // `questionnaireCompleted` is an instruction rather than a field: the server answers it with a
  // timestamp, so it has no place in the document this returns.
  const { questionnaireCompleted, ...fields } = pending;
  void questionnaireCompleted;

  return { ...profile, ...fields };
}

function withoutSettled(current: ProfileUpdate, sent: ProfileUpdate): ProfileUpdate {
  const next: Record<string, unknown> = { ...current };

  for (const [key, value] of Object.entries(sent)) {
    if (JSON.stringify(next[key]) === JSON.stringify(value)) {
      delete next[key];
    }
  }

  return next as ProfileUpdate;
}
